import { Fragment, useEffect, useRef, useState } from "react";
import { useModalFocus } from "../logic/useModalFocus";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../api/client";
import { AppHeader, Breadcrumb } from "../components/AppHeader";
import { CurrencyIcon } from "../components/CurrencyIcon";
import { ItemSprite } from "../components/ItemSprite";
import { Sigil } from "../components/Sigil";
import { useResponsiveLayout } from "../logic/useResponsiveLayout";
import { playSfx } from "../logic/sfx";
import { factionFor } from "../theme/factions";
import { E32 } from "../theme/endesga32";
import { pixelTheme } from "../theme/pixel";
import { simplePanelStyle } from "../theme/simplePanel";

// ---------------------------------------------------------------------
// The Antar Battle Arena bracket page.
//
// Faithful port of `Battle Arena/arena-app.jsx` + `arena-style.css`,
// minus the page-shell header/footer (which the user explicitly told
// me to skip — the app already has its own AppHeader and no footer).
//
// Constraints from `docs/frontend/NOTES.md`:
//   * 100% inline styles, Endesga-32 palette via `E32`.
//   * No `aspect-ratio` outside Card.tsx, no `vw/vh`, no `clamp/min/max`.
//
// The reference CSS uses `aspect-ratio: 1/1` on `.opp-portrait-wrap`;
// since the parent column is a fixed 360px and the panel padding is
// 16px, the portrait wrap is a known 328×328 square — encoded as
// fixed px here to stay inside the allowlist.

const C_PARCHMENT = E32.parchment;
const C_TAN = E32.tan;
const C_AMBER = E32.amber;
const C_BUTTER = E32.butter;
const C_RUBY = E32.ruby;
const C_PLUM = E32.plum;
const C_OXBLOOD = E32.oxblood;
const C_FERN = E32.fern;
const C_FOREST = E32.forest;
const C_RUST = E32.rust;
const C_INK = E32.ink;
const C_COBALT = E32.cobalt;
const C_MIST = E32.mist;
// Light tint used for void-juice amount text on the dark hero
// background — `E32.mulberry` (the wallet's "VJ" color) is too dim
// against the inky pit, so use rose for readability.
const C_COBALT_LIGHT = E32.rose;

// Bitmap shadow stack matching `--shadow-bitmap-2` from the reference
// (two ink steps), pulled out so panels and the hero share one
// definition.
const SHADOW_BITMAP_2 = `2px 2px 0 0 ${C_INK}, 4px 4px 0 0 ${C_INK}`;

export function ArenaPage({
  title: _title,
  breadcrumb,
  parentPath,
}: {
  title: string;
  breadcrumb: string;
  parentPath: string;
}) {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const layout = useResponsiveLayout();
  const [user, setUser] = useState<api.User | null>(null);
  const [state, setState] = useState<api.ArenaState | null>(null);
  const [decks, setDecks] = useState<api.DeckSummary[] | null>(null);
  const [selectedDeckId, setSelectedDeckId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showRewardsModal, setShowRewardsModal] =
    useState<api.ArenaClaimResponse | null>(null);
  const [confirmAbandon, setConfirmAbandon] = useState(false);

  const refresh = async () => {
    const [me, { arena }, decksResp] = await Promise.all([
      api.getMe(),
      api.getArenaState(),
      api.listDecks(),
    ]);
    setUser(me.user);
    setState(arena);
    setDecks(decksResp.decks);
    if (selectedDeckId == null && decksResp.decks.length > 0) {
      const locked = arena.run?.locked_deck_id ?? null;
      setSelectedDeckId(locked ?? decksResp.decks[0].id);
    }
  };

  useEffect(() => {
    void refresh().catch((e) =>
      setError(e instanceof Error ? e.message : "Failed to load arena state."),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = state?.run ?? null;
  const lockedDeckId = run?.locked_deck_id ?? null;
  const wins = run?.wins_count ?? 0;
  const currentRound = run?.current_round ?? 1;
  const total = 10;
  const isCleared = run != null && wins >= 10;
  const isLost = run != null && run.status === "lost";
  const inActiveRound =
    run != null &&
    !isCleared &&
    !isLost &&
    currentRound >= 1 &&
    currentRound <= total;
  const preFight = inActiveRound && wins === 0 && currentRound === 1;
  const ticketCount = state?.ticket_count ?? 0;

  async function onEnrol() {
    setBusy(true);
    setError(null);
    try {
      await api.enrolInArena();
      await refresh();
    } catch (e) {
      setError(errorMessage(e, "Couldn't enrol."));
    } finally {
      setBusy(false);
    }
  }

  async function onCollect() {
    setBusy(true);
    setError(null);
    try {
      await api.collectArenaHeld();
      await refresh();
    } catch (e) {
      setError(errorMessage(e, "Couldn't collect — make space in your bag."));
    } finally {
      setBusy(false);
    }
  }

  async function onStartRound() {
    if (selectedDeckId == null) {
      setError("Pick a deck first.");
      return;
    }
    const chosen = decks?.find((d) => d.id === selectedDeckId);
    if (!chosen) {
      setError("Deck not found.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await api.startArenaRound(api.toDeckRef(chosen));
      navigate(`/battle/${r.engine_session_id}`, {
        state: { returnTo: "/world/antar/arena/floor" },
      });
    } catch (e) {
      setError(errorMessage(e, "Couldn't start round."));
      setBusy(false);
    }
  }

  async function onClaim(kind: api.ArenaClaimKind) {
    setBusy(true);
    setError(null);
    try {
      const r = await api.claimArena(kind);
      setShowRewardsModal(r);
      await refresh();
    } catch (e) {
      setError(errorMessage(e, "Couldn't claim rewards."));
    } finally {
      setBusy(false);
    }
  }

  function onConfirmRewardsModal() {
    setShowRewardsModal(null);
    // Stay on the bracket; the user can re-enrol from here.
  }

  const opponentForRound = (n: number): api.ArenaOpponent | null => {
    if (!run) return null;
    if (n === run.current_round) return run.current_opponent;
    if (n === (run.current_round ?? 0) + 1) return run.next_opponent;
    return null;
  };

  const rewardsTable = state?.rewards_table ?? [];
  const fightDisabled = busy || selectedDeckId == null;

  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        background: theme.pageBg,
        color: theme.ink,
        fontFamily: theme.fontBody,
        padding: "20px 24px",
      }}
    >
      <div
        style={{
          maxWidth: 1040,
          margin: "0 auto",
          display: "grid",
          gap: 18,
        }}
      >
        <AppHeader
          user={user}
          backTo={parentPath}
          backLabel="◀ BACK"
        />
        <Breadcrumb text={breadcrumb} />

        {error && <ErrorBanner text={error} />}

        <HeroPanel
          currentRound={Math.min(currentRound, total)}
          total={total}
          wins={wins}
          isCleared={isCleared}
          isLost={isLost}
          hasRun={run != null}
          rewardsTable={rewardsTable}
          opponentForRound={opponentForRound}
        />

        {/* Two-col row is only meaningful while a run is in progress.
            Stacks vertically once the viewport drops below 800px so
            the deck list isn't squeezed against a fixed 360px column
            (matches the project-wide `narrow` breakpoint). */}
        {run && inActiveRound && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: layout.narrow ? "minmax(0, 1fr)" : "360px minmax(0, 1fr)",
              gap: 16,
              alignItems: "start",
            }}
          >
            <OpponentPanel
              opponent={run.current_opponent}
              currentRound={currentRound}
              total={total}
              fixedWidth={!layout.narrow}
            />
            <DeckListPanel
              decks={decks}
              selectedDeckId={lockedDeckId ?? selectedDeckId}
              lockedDeckId={lockedDeckId}
              onSelect={(d) => {
                if (lockedDeckId == null) setSelectedDeckId(d.id);
              }}
            />
          </div>
        )}

        {/* Bottom bar — state machine: pre-fight enrol, mid-run fight
            block, end-state claim. */}
        {!run && (
          <PreEnrolBar
            ticketCount={ticketCount}
            heldItems={state?.held.items ?? []}
            busy={busy}
            onEnrol={() => void onEnrol()}
            onCollect={() => void onCollect()}
            onGoToClerk={() => navigate("/world/antar/arena/clerk")}
          />
        )}

        {run && inActiveRound && (
          <FightBlock
            user={user}
            preFight={preFight}
            busy={busy}
            fightDisabled={fightDisabled}
            onFight={() => void onStartRound()}
            onAbandon={() => setConfirmAbandon(true)}
          />
        )}

        {run && isCleared && (
          <FinalBar
            label="CLAIM REWARDS"
            busy={busy}
            onClick={() => void onClaim("cleared")}
          />
        )}
        {run && isLost && (
          <DefeatBlock
            wins={wins}
            busy={busy}
            onClaim={() => void onClaim("lost")}
          />
        )}
      </div>

      {showRewardsModal && (
        <RewardsModal payload={showRewardsModal} onClose={onConfirmRewardsModal} />
      )}
      {confirmAbandon && (
        <ConfirmAbandonModal
          wins={wins}
          rewardsTable={rewardsTable}
          currentRound={currentRound}
          onCancel={() => setConfirmAbandon(false)}
          onConfirm={() => {
            setConfirmAbandon(false);
            void onClaim("abandoned");
          }}
        />
      )}
    </div>
  );
}

// ─── Hero panel (dark "pit") ────────────────────────────────────────

function HeroPanel({
  currentRound,
  total,
  wins,
  isCleared,
  isLost,
  hasRun,
  rewardsTable,
  opponentForRound,
}: {
  currentRound: number;
  total: number;
  wins: number;
  isCleared: boolean;
  isLost: boolean;
  hasRun: boolean;
  rewardsTable: api.ArenaRewardSlot[];
  opponentForRound: (n: number) => api.ArenaOpponent | null;
}) {
  const theme = pixelTheme;
  return (
    <section
      style={{
        position: "relative",
        background: C_INK,
        border: `3px solid ${C_INK}`,
        boxShadow: `inset 0 0 0 2px ${C_PLUM}, inset 0 0 0 4px ${C_INK}, inset 0 0 0 6px ${C_OXBLOOD}, ${SHADOW_BITMAP_2}`,
        padding: "24px 28px 24px",
        color: C_PARCHMENT,
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `repeating-linear-gradient(45deg, rgba(162, 38, 51, 0.08) 0 2px, transparent 2px 8px)`,
          pointerEvents: "none",
        }}
      />
      <div style={{ position: "relative", zIndex: 1 }}>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 9,
            letterSpacing: "0.18em",
            color: C_AMBER,
            textTransform: "uppercase",
            marginBottom: 10,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: 6,
              height: 6,
              background: C_AMBER,
            }}
          />
          <span>ANTAR CITY BATTLE ARENA</span>
          <span style={{ color: C_TAN, opacity: 0.6 }}>·</span>
          <span style={{ color: C_TAN }}>
            {!hasRun
              ? "AWAITING ENROLMENT"
              : isCleared
                ? "CLEARED"
                : isLost
                  ? "ENDED"
                  : `ROUND ${currentRound} OF ${total}`}
          </span>
        </div>
        <h1
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 42,
            letterSpacing: "0.04em",
            color: C_PARCHMENT,
            textShadow: `3px 3px 0 ${C_OXBLOOD}, 6px 6px 0 ${C_PLUM}`,
            margin: "0 0 12px",
            lineHeight: 1,
          }}
        >
          BATTLE ARENA
        </h1>

        <RulesGuide />

        <BattlePassTrack
          total={total}
          currentRound={currentRound}
          wins={wins}
          isCleared={isCleared}
          isLost={isLost}
          rewardsTable={rewardsTable}
          opponentForRound={opponentForRound}
        />
      </div>
    </section>
  );
}

// ─── Rules guide (4 rule chips inside hero) ─────────────────────────

function RulesGuide() {
  const theme = pixelTheme;
  const layout = useResponsiveLayout();
  const rules: { glyph: string; bg: string; fg: string; text: string }[] = [
    { glyph: "+", bg: C_RUST, fg: C_PARCHMENT, text: "No healing between rounds" },
    { glyph: "≡", bg: C_COBALT, fg: C_PARCHMENT, text: "No deck swap after round 1" },
    { glyph: "✓", bg: C_FOREST, fg: C_PARCHMENT, text: "Win locks in previous rewards" },
    { glyph: "✗", bg: C_OXBLOOD, fg: C_AMBER, text: "Lose forfeits at-risk rewards" },
  ];
  // 4 across normally; on narrow viewports stack 2×2 so each rule
  // has enough room for its sentence.
  const cols = layout.narrow ? 2 : 4;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gap: 10,
        margin: "6px 0 22px",
      }}
    >
      {rules.map((r) => (
        <div
          key={r.glyph}
          style={{
            display: "grid",
            gridTemplateColumns: "32px minmax(0, 1fr)",
            alignItems: "center",
            gap: 10,
            padding: "10px 12px",
            background: "rgba(255, 255, 255, 0.03)",
            border: `1px solid ${C_PLUM}`,
          }}
        >
          <span
            style={{
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: theme.fontDisplay,
              fontSize: 14,
              color: r.fg,
              background: r.bg,
              border: `2px solid ${C_INK}`,
              boxShadow: `inset 2px 2px 0 rgba(255,255,255,0.18), inset -2px -2px 0 rgba(0,0,0,0.30)`,
            }}
          >
            {r.glyph}
          </span>
          <span
            style={{
              fontFamily: theme.fontBody,
              fontSize: 15,
              color: C_PARCHMENT,
              lineHeight: 1.25,
              letterSpacing: "0.02em",
            }}
          >
            {r.text}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Battle Pass Track ──────────────────────────────────────────────

type OrbStatus = "locked-in" | "at-risk" | "next" | "future";

function statusFor(
  round: number,
  currentRound: number,
  wins: number,
  isCleared: boolean,
  isLost: boolean,
): OrbStatus {
  if (isCleared) return "locked-in";
  if (isLost) {
    if (round <= wins - 1) return "locked-in";
    if (round === wins) return "at-risk";
    return "future";
  }
  if (round <= wins - 1) return "locked-in";
  if (wins >= 1 && round === wins) return "at-risk";
  if (round === currentRound) return "next";
  return "future";
}

function BattlePassTrack({
  total,
  currentRound,
  wins,
  isCleared,
  isLost,
  rewardsTable,
  opponentForRound,
}: {
  total: number;
  currentRound: number;
  wins: number;
  isCleared: boolean;
  isLost: boolean;
  rewardsTable: api.ArenaRewardSlot[];
  opponentForRound: (n: number) => api.ArenaOpponent | null;
}) {
  const layout = useResponsiveLayout();
  // Rail spans from the first orb's center to the last orb's center.
  // With N equal cells, orb centers sit at (i+0.5)/N → pad 50/N% each
  // side. Fill covers `wins` cleared segments.
  const railPadPct = 50 / total;
  const railWidthPct = 100 - 2 * railPadPct;
  const filledRounds = isCleared ? total : wins;
  const fillWPct = (railWidthPct * Math.max(0, filledRounds)) / total;
  // On narrow viewports the 10 orbs + labels need at least ~640px to
  // breathe. Make the track horizontally scrollable when the parent
  // can't accommodate; on wide viewports it lays out flush.
  const trackMinWidth = layout.narrow ? 640 : undefined;

  return (
    <div
      style={{
        background: "rgba(0,0,0,0.32)",
        border: `2px solid ${C_PLUM}`,
        padding: "18px 22px",
        overflowX: layout.narrow ? "auto" : "visible",
      }}
    >
     <div style={{ minWidth: trackMinWidth }}>
      <div style={{ position: "relative", height: 84, marginTop: 30 }}>
        <div
          style={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            height: 12,
            left: `${railPadPct}%`,
            right: `${railPadPct}%`,
            background: C_PLUM,
            border: `2px solid ${C_INK}`,
            boxShadow: `inset 0 2px 0 rgba(0,0,0,0.32), inset 0 -2px 0 rgba(255,255,255,0.08)`,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            height: 12,
            left: `${railPadPct}%`,
            width: `${fillWPct}%`,
            background: C_FERN,
            border: `2px solid ${C_INK}`,
            boxShadow: `inset 0 2px 0 rgba(255,255,255,0.25), inset 0 -2px 0 rgba(0,0,0,0.32)`,
            transition: "width 240ms ease-out",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))`,
            alignItems: "center",
            zIndex: 2,
          }}
        >
          {Array.from({ length: total }).map((_, i) => {
            const round = i + 1;
            const status = statusFor(round, currentRound, wins, isCleared, isLost);
            const reward = rewardsTable[i] ?? null;
            const isBoss = round === total;
            return (
              <BpOrbCell
                key={round}
                status={status}
                isBoss={isBoss}
                reward={reward}
              />
            );
          })}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))`,
          marginTop: 8,
          gap: 4,
        }}
      >
        {Array.from({ length: total }).map((_, i) => {
          const round = i + 1;
          const status = statusFor(round, currentRound, wins, isCleared, isLost);
          const reward = rewardsTable[i] ?? null;
          const opp = opponentForRound(round);
          const isBoss = round === total;
          return (
            <BpLabel
              key={round}
              status={status}
              reward={reward}
              opp={opp}
              isBoss={isBoss}
            />
          );
        })}
      </div>
     </div>
    </div>
  );
}

function BpOrbCell({
  status,
  isBoss,
  reward,
}: {
  status: OrbStatus;
  isBoss: boolean;
  reward: api.ArenaRewardSlot | null;
}) {
  const theme = pixelTheme;
  const isNext = status === "next";

  const palette = (() => {
    switch (status) {
      case "locked-in":
        return { bg: C_FERN, border: C_FOREST, color: C_PARCHMENT };
      case "at-risk":
        return { bg: C_AMBER, border: C_RUST, color: C_INK };
      case "next":
        return { bg: C_RUBY, border: C_BUTTER, color: C_BUTTER };
      case "future":
      default:
        return isBoss
          ? { bg: C_PLUM, border: C_AMBER, color: C_AMBER }
          : { bg: C_OXBLOOD, border: C_TAN, color: C_TAN };
    }
  })();

  const size = isNext ? 64 : 52;
  // Inner glyph matches the JSX:
  //   locked-in → ✓ (large), at-risk → ! (bold), next → ▶,
  //   future boss → ◆, future item-round → item glyph, gold → ●.
  const inner = (() => {
    if (status === "locked-in") return <span style={{ fontSize: 18 }}>✓</span>;
    if (status === "at-risk")
      return <span style={{ fontSize: 18, fontWeight: 700 }}>!</span>;
    if (status === "next") return <span style={{ fontSize: 22 }}>▶</span>;
    if (isBoss) return <span style={{ fontSize: 18 }}>◆</span>;
    if (reward && reward.items.length > 0)
      return <span style={{ fontSize: 16 }}>◆</span>;
    return <span style={{ fontSize: 6 }}>●</span>;
  })();

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        className={isNext ? "arena-bp-orb-next" : undefined}
        style={{
          width: size,
          height: size,
          background: palette.bg,
          border: `${isNext ? 3 : 2}px solid ${palette.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: theme.fontDisplay,
          color: palette.color,
          position: "relative",
          transform: isNext ? "translateY(-3px)" : undefined,
          // The `next` orb's box-shadow is owned by the CSS keyframes
          // (the pulse animates it). For other orbs we set it inline.
          boxShadow: isNext
            ? undefined
            : status === "future"
              ? `inset 2px 2px 0 rgba(255,255,255,0.10), inset -2px -2px 0 rgba(0,0,0,0.30), 2px 2px 0 ${C_INK}`
              : `inset 2px 2px 0 rgba(255,255,255,0.4), inset -2px -2px 0 rgba(0,0,0,0.20), 3px 3px 0 ${C_INK}`,
        }}
      >
        {inner}
      </div>
    </div>
  );
}

function BpLabel({
  status,
  reward,
  opp,
  isBoss,
}: {
  status: OrbStatus;
  reward: api.ArenaRewardSlot | null;
  opp: api.ArenaOpponent | null;
  isBoss: boolean;
}) {
  const theme = pixelTheme;
  const statusText =
    status === "locked-in"
      ? "LOCKED ✓"
      : status === "at-risk"
        ? "AT RISK"
        : status === "next"
          ? "FIGHT NEXT"
          : "";
  const statusColor =
    status === "locked-in"
      ? C_FERN
      : status === "at-risk"
        ? C_AMBER
        : status === "next"
          ? C_BUTTER
          : isBoss
            ? C_AMBER
            : C_TAN;
  const oppColor = status === "next" ? C_PARCHMENT : C_TAN;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        padding: "0 2px",
        textAlign: "center",
      }}
    >
      {/* Status FIRST, then name. Each row has a fixed height so the
          "FIGHT NEXT" pip and the names align horizontally across
          every column, independent of reward content below. */}
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 8,
          letterSpacing: "0.10em",
          color: statusColor,
          textTransform: "uppercase",
          lineHeight: 1,
          height: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {statusText}
      </div>
      <div
        style={{
          fontFamily: theme.fontBody,
          fontSize: 14,
          color: oppColor,
          lineHeight: 1.1,
          height: 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          letterSpacing: "0.02em",
        }}
      >
        {opp ? opp.name ?? humanizeNpcId(opp.npc_id) : isBoss ? "BOSS" : "—"}
      </div>
      {/* Reward chips — each row is a 2-column grid (icon | amount)
          and the whole block centers in the column. The grid keeps
          icons left-aligned with each other across rows so a gold
          row + VJ row sit on the same vertical axis. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "16px auto",
          columnGap: 4,
          rowGap: 2,
          justifyContent: "center",
          alignItems: "center",
          fontFamily: theme.fontDisplay,
          fontSize: 9,
          letterSpacing: "0.04em",
          lineHeight: 1,
          marginTop: 2,
        }}
      >
        {reward?.currencies.map((c, i) => {
          const isGold = c.currency === "gold";
          const colour = isGold ? C_AMBER : C_COBALT_LIGHT;
          return (
            <Fragment key={`c${i}`}>
              <CurrencyIcon
                kind={isGold ? "gold" : "juice"}
                size={16}
                tint={colour}
              />
              <span style={{ color: colour, justifySelf: "start" }}>
                +{c.amount}
                {isGold ? "G" : "mL"}
              </span>
            </Fragment>
          );
        })}
      </div>
      {/* Item reward — rendered larger than the currency chips, as the
          one fixed icon for the row. The 44px size matches the
          inventory grid so the player recognises the art. */}
      {reward?.items.length ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            marginTop: 4,
          }}
        >
          {reward.items.map((it, i) => (
            <div
              key={`i${i}`}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <ItemSprite id={it.item_id} size={44} />
              <span
                style={{
                  fontFamily: theme.fontDisplay,
                  fontSize: 9,
                  color: C_PARCHMENT,
                  letterSpacing: "0.04em",
                  lineHeight: 1,
                }}
              >
                {shortItemLabel(it.item_id)}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function humanizeNpcId(id: string): string {
  const tail = id.replace(/^arena_/, "").replace(/_/g, " ");
  return tail.replace(/\b\w/g, (c) => c.toUpperCase());
}

// Short label for the BP reward chip — just one or two words to fit
// under the 1/10-width orb cell. The booster packs are the only items
// authored today; if more land here, extend the table.
function shortItemLabel(itemId: string): string {
  switch (itemId) {
    case "human_booster_pack":
      return "HUMAN PACK";
    case "dwarf_booster_pack":
      return "DWARF PACK";
    case "goblin_booster_pack":
      return "GOBLIN PACK";
    case "elf_booster_pack":
      return "ELF PACK";
    case "orc_booster_pack":
      return "ORC PACK";
    case "high_fantasia_pack":
      return "PACK";
    default:
      return itemId
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

// ─── Opponent panel ─────────────────────────────────────────────────

function OpponentPanel({
  opponent,
  currentRound,
  total,
  fixedWidth,
}: {
  opponent: api.ArenaOpponent | null;
  currentRound: number;
  total: number;
  /** When true (wide viewport), the parent column is a fixed 360px;
   *  we hardcode the portrait to its 328-px square. When false
   *  (narrow stack), the column is fluid — render the portrait at a
   *  smaller fixed size so it doesn't dominate the page on a phone. */
  fixedWidth: boolean;
}) {
  const theme = pixelTheme;
  // 328 = 360 (column width) − 16×2 (panel padding) on wide layout.
  // 240 is a safe smaller square for the narrow stack — leaves room
  // for the corner stamp + bottom label without the parent forcing
  // overflow.
  const portraitSize = fixedWidth ? 328 : 240;
  // Faction now flows off the project-level speaker catalog (wire
  // field `opponent.faction`); falls back to humans if missing so
  // the tint still resolves.
  const factionKey = opponent?.faction ?? "humans";
  const f = factionFor(factionKey);
  const displayName = opponent?.name ?? (opponent ? humanizeNpcId(opponent.npc_id) : "—");

  const tierStars = (() => {
    switch (opponent?.tier) {
      case "easy":
        return 1;
      case "medium":
        return 2;
      case "hard":
        return 3;
      case "boss":
        return 5;
      default:
        return 0;
    }
  })();
  const titleByTier = (() => {
    switch (opponent?.tier) {
      case "easy":
        return "INITIATE";
      case "medium":
        return "VETERAN";
      case "hard":
        return "CHAMPION";
      case "boss":
        return "WARLORD";
      default:
        return "—";
    }
  })();

  return (
    <PanelShell title="NEXT OPPONENT" hint={`ROUND ${currentRound} OF ${total}`}>
      <div style={{ display: "grid", gap: 12 }}>
        <div
          style={{
            position: "relative",
            width: portraitSize,
            height: portraitSize,
            border: `3px solid ${C_INK}`,
            // Reference: `.opp-portrait-wrap` mixes faction colour at
            // 18% over parchment via color-mix(). We approximate by
            // stacking a faction-tinted overlay (opacity 0.18) over
            // a parchment base — visually close, exact mix differs.
            backgroundImage: `repeating-linear-gradient(45deg, rgba(0,0,0,0.08) 0 4px, transparent 4px 8px)`,
            backgroundColor: C_PARCHMENT,
            overflow: "hidden",
          }}
        >
          {/* Faction tint */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: f.color,
              opacity: 0.18,
              pointerEvents: "none",
            }}
          />
          {/* Real NPC portrait when authored, sigil fallback otherwise.
              Wrapped in a full-bleed flex container so the
              absolutely-positioned siblings (corner stamp, bottom
              label) can't perturb the centering. */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <OpponentArt
              opponent={opponent}
              factionKey={factionKey}
              sigilInk={f.sigilInk}
              size={portraitSize}
            />
          </div>
          {/* Corner stamp */}
          <div
            style={{
              position: "absolute",
              top: 6,
              right: 6,
              width: 40,
              height: 40,
              background: C_INK,
              border: `2px solid ${C_PARCHMENT}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 6,
            }}
          >
            <Sigil faction={factionKey} size={24} color={C_PARCHMENT} />
          </div>
          {/* Bottom label */}
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              padding: "6px 10px",
              background: C_INK,
              color: C_PARCHMENT,
              fontFamily: theme.fontDisplay,
              fontSize: 9,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{titleByTier}</span>
            <span>{opponent ? `R${opponent.round}` : ""}</span>
          </div>
        </div>

        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 16,
            letterSpacing: "0.04em",
            color: theme.ink,
            lineHeight: 1.3,
            textTransform: "uppercase",
          }}
        >
          {displayName}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontFamily: theme.fontDisplay,
              fontSize: 9,
              color: theme.inkFaint,
              letterSpacing: "0.08em",
            }}
          >
            <Sigil faction={factionKey} size={16} />
            <span>{factionKey.toUpperCase()}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontFamily: theme.fontDisplay,
                fontSize: 8,
                color: theme.inkFaint,
                letterSpacing: "0.10em",
              }}
            >
              DIFFICULTY
            </span>
            <Stars filled={tierStars} />
          </div>
        </div>
      </div>
    </PanelShell>
  );
}

// Pixel-art difficulty stars. The PNG at `public/pixel_star_white.png`
// is white-on-transparent; we tint via CSS mask the same way `<Sigil>`
// does, so a single asset paints amber for filled and a desaturated
// steel for empty.
// Opponent art — same fall-through pattern as `<NpcPortrait>`:
// `/portraits/npcs/<id>_<MOOD>.png` first, then `/portraits/npcs/<id>.png`,
// finally the faction sigil as a fallback. Sized 180×180 inside the
// 328×328 wrap so the image breathes against the corner stamp + bottom
// label without spilling under them.
function OpponentArt({
  opponent,
  factionKey,
  sigilInk,
  size = 180,
}: {
  opponent: api.ArenaOpponent | null;
  factionKey: string;
  sigilInk: string;
  size?: number;
}) {
  const npcId = opponent?.npc_id ?? null;
  const mood = opponent?.default_mood ?? null;
  const candidates: string[] = npcId
    ? [
        ...(mood ? [`/portraits/npcs/${npcId}_${mood.toUpperCase()}.png`] : []),
        `/portraits/npcs/${npcId}.png`,
      ]
    : [];
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    setAttempt(0);
  }, [npcId, mood]);
  const src = candidates[attempt] ?? null;

  if (src) {
    // Real portrait art is composed to fill the frame edge-to-edge —
    // unlike the Sigil fallback, which is a small mark centered in
    // the wrap. Use the full wrap size so the pixel art doesn't sit
    // in the middle with parchment around it.
    return (
      <img
        src={src}
        alt=""
        draggable={false}
        onError={() => setAttempt((n) => n + 1)}
        style={{
          width: size,
          height: size,
          imageRendering: "pixelated",
          objectFit: "contain",
        }}
      />
    );
  }
  // Sigil is a centered mark — render it at ~55% of the wrap so the
  // parchment shows around it, matching the pre-portrait look.
  return (
    <Sigil faction={factionKey} size={Math.round(size * 0.55)} color={sigilInk} />
  );
}

function Stars({ filled, total = 5 }: { filled: number; total?: number }) {
  return (
    <span style={{ display: "inline-flex", gap: 3 }}>
      {Array.from({ length: total }).map((_, i) => {
        const isOn = i < filled;
        return (
          <span
            key={i}
            aria-hidden
            style={{
              // Native size of `/pixel_star_white.png` is 17×17, so
              // we render at 17 to avoid sub-pixel scaling.
              display: "inline-block",
              width: 17,
              height: 17,
              backgroundColor: isOn ? C_AMBER : C_MIST,
              opacity: isOn ? 1 : 0.45,
              WebkitMaskImage: `url(/pixel_star_white.png)`,
              maskImage: `url(/pixel_star_white.png)`,
              WebkitMaskRepeat: "no-repeat",
              maskRepeat: "no-repeat",
              WebkitMaskPosition: "center",
              maskPosition: "center",
              WebkitMaskSize: "contain",
              maskSize: "contain",
              flexShrink: 0,
            }}
          />
        );
      })}
    </span>
  );
}

// ─── Deck list panel ────────────────────────────────────────────────

function DeckListPanel({
  decks,
  selectedDeckId,
  lockedDeckId,
  onSelect,
}: {
  decks: api.DeckSummary[] | null;
  selectedDeckId: number | null;
  lockedDeckId: number | null;
  onSelect: (deck: api.DeckSummary) => void;
}) {
  const theme = pixelTheme;
  return (
    <PanelShell
      title="CHOOSE YOUR DECK"
      hint={lockedDeckId != null ? "LOCKED FOR THIS RUN" : "NO SWAPS MID-RUN"}
    >
      {decks === null ? (
        <div style={{ color: theme.inkFaint }}>Loading…</div>
      ) : decks.length === 0 ? (
        <div style={{ color: E32.scarlet, fontSize: 14 }}>
          You don't have any decks.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 6 }}>
          {decks.map((d) => {
            const selected = d.id === selectedDeckId;
            const locked = lockedDeckId != null && lockedDeckId !== d.id;
            const f = factionFor(d.faction);
            return (
              <button
                key={d.id}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-disabled={locked}
                // Native `disabled` would swallow the click; use
                // `aria-disabled` so the handler still fires and we can
                // give audible feedback when the user picks a locked row.
                onClick={() => {
                  if (locked) {
                    playSfx("ai_notice_or_warning_sound");
                    return;
                  }
                  onSelect(d);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 12px",
                  background: selected ? "rgba(99, 199, 77, 0.10)" : theme.panel,
                  border: `2px solid ${selected ? C_FERN : theme.panelEdge}`,
                  borderLeft: `8px solid ${f.color}`,
                  cursor: locked ? "not-allowed" : "pointer",
                  position: "relative",
                  textAlign: "left",
                  width: "100%",
                  opacity: locked ? 0.45 : 1,
                  boxShadow: selected
                    ? `0 0 0 2px ${C_FERN}, 2px 2px 0 0 ${C_INK}`
                    : undefined,
                }}
              >
                <Sigil faction={d.faction} size={28} color={f.sigilInk} />
                <div
                  style={{
                    flex: "1 1 auto",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      fontFamily: theme.fontBody,
                      fontSize: 20,
                      color: theme.ink,
                    }}
                  >
                    {d.name}
                  </span>
                  <span
                    style={{
                      fontFamily: theme.fontDisplay,
                      fontSize: 9,
                      letterSpacing: "0.08em",
                      color: theme.inkFaint,
                      textTransform: "uppercase",
                    }}
                  >
                    {d.faction.toUpperCase()} · 30 CARDS
                  </span>
                </div>
                {selected && (
                  <span
                    style={{
                      fontFamily: theme.fontDisplay,
                      fontSize: 8,
                      letterSpacing: "0.08em",
                      padding: "4px 6px",
                      background: C_FERN,
                      color: C_PARCHMENT,
                      textTransform: "uppercase",
                    }}
                  >
                    SELECTED ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </PanelShell>
  );
}

function PanelShell({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: ReactNode;
}) {
  const theme = pixelTheme;
  return (
    <section style={{ ...simplePanelStyle(theme), padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 12,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: "0.12em",
            color: theme.ink,
            textTransform: "uppercase",
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 8,
            letterSpacing: "0.10em",
            color: theme.inkFaint,
            textTransform: "uppercase",
          }}
        >
          {hint}
        </div>
      </div>
      {children}
    </section>
  );
}

// ─── Bottom-bar state machine ───────────────────────────────────────

function PreEnrolBar({
  ticketCount,
  heldItems,
  busy,
  onEnrol,
  onCollect,
  onGoToClerk,
}: {
  ticketCount: number;
  heldItems: api.ArenaHeldItem[];
  busy: boolean;
  onEnrol: () => void;
  onCollect: () => void;
  onGoToClerk: () => void;
}) {
  const theme = pixelTheme;
  const hasTickets = ticketCount > 0;
  const hasHeld = heldItems.length > 0;
  // The label is the *cost* — one ticket per enrolment, regardless
  // of how many the player is carrying. The button is disabled below
  // when ticketCount is 0.
  const enrolLabel = `ENROL (1 ARENA TICKET)`;

  return (
    <section
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        gap: 14,
        alignItems: "center",
        padding: "16px 18px",
        ...simplePanelStyle(theme),
      }}
    >
      <div
        style={{
          fontFamily: theme.fontBody,
          fontSize: 16,
          color: theme.ink,
          lineHeight: 1.3,
        }}
      >
        {hasHeld ? (
          <>
            Thomas Anton is holding{" "}
            <b>
              {heldItems
                .map((h) => `${h.count}× ${humanizeNpcId(h.item_id)}`)
                .join(", ")}
            </b>{" "}
            from your last bout — collect before you re-enrol.
          </>
        ) : hasTickets ? (
          <>Ready when you are. One ticket per run.</>
        ) : (
          <>You'll need an Arena Ticket. Buy one from Thomas Anton (300 gold).</>
        )}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {hasHeld ? (
          <PixelButton kind="primary" large onClick={onCollect} disabled={busy}>
            {busy ? "COLLECTING…" : "COLLECT WINNINGS"}
          </PixelButton>
        ) : hasTickets ? (
          <>
            <PixelButton onClick={onGoToClerk} disabled={busy}>
              CLERK
            </PixelButton>
            <PixelButton kind="primary" large onClick={onEnrol} disabled={busy}>
              {busy ? "ENROLLING…" : enrolLabel}
            </PixelButton>
          </>
        ) : (
          <PixelButton kind="primary" large onClick={onGoToClerk}>
            BUY TICKET @ CLERK
          </PixelButton>
        )}
      </div>
    </section>
  );
}

function FightBlock({
  user,
  preFight,
  busy,
  fightDisabled,
  onFight,
  onAbandon,
}: {
  user: api.User | null;
  preFight: boolean;
  busy: boolean;
  fightDisabled: boolean;
  onFight: () => void;
  onAbandon: () => void;
}) {
  const theme = pixelTheme;
  const hp = user ? api.liveHp(user.sleep) : 0;
  const hpMax = user?.sleep.max_hp ?? 100;
  const pct = Math.max(0, Math.min(100, (hp / hpMax) * 100));
  const hpClass = pct <= 25 ? "crit" : pct <= 55 ? "warn" : "ok";
  const hpBg =
    hpClass === "crit" ? C_RUST : hpClass === "warn" ? C_AMBER : C_FERN;

  return (
    <section
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto auto",
        gap: 14,
        alignItems: "center",
        padding: "16px 18px",
        ...simplePanelStyle(theme),
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 9,
            letterSpacing: "0.10em",
            color: theme.inkFaint,
            textTransform: "uppercase",
          }}
        >
          YOUR HP
        </span>
        <div
          style={{
            position: "relative",
            width: 200,
            height: 22,
            background: C_PLUM,
            border: `2px solid ${C_INK}`,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              width: `${pct}%`,
              background: hpBg,
              boxShadow: `inset 0 2px 0 rgba(255,255,255,0.22), inset 0 -2px 0 rgba(0,0,0,0.32)`,
              transition: "width 240ms ease-out, background 200ms",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: theme.fontDisplay,
              fontSize: 10,
              color: C_PARCHMENT,
              textShadow: `1px 1px 0 ${C_INK}`,
              letterSpacing: "0.06em",
            }}
          >
            {hp} / {hpMax}
          </div>
        </div>
      </div>
      <PixelButton
        kind="danger"
        large
        onClick={onAbandon}
        disabled={busy}
        title={preFight ? "Forfeit the ticket" : "Walk away with the locked-in rewards"}
      >
        {preFight ? "ABANDON" : "CLAIM & LEAVE"}
      </PixelButton>
      <PixelButton kind="primary" large onClick={onFight} disabled={fightDisabled}>
        {busy ? "STARTING…" : "FIGHT"}
      </PixelButton>
    </section>
  );
}

// Defeat card — single panel that combines the "you lost" copy and
// the Claim & Leave button so the post-loss state is one bar, not two.
function DefeatBlock({
  wins,
  busy,
  onClaim,
}: {
  wins: number;
  busy: boolean;
  onClaim: () => void;
}) {
  const theme = pixelTheme;
  const headline =
    wins === 0 ? "DEFEATED IN ROUND 1." : `DEFEATED IN ROUND ${wins + 1}.`;
  const sub =
    wins === 0
      ? "The crowd boos. Nothing was locked in — your ticket is gone, but so is the run."
      : wins === 1
        ? "One round locked in. Your at-risk reward is forfeit."
        : `${wins - 1} rounds locked in. Your at-risk reward from round ${wins} is forfeit.`;
  return (
    <section
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        gap: 14,
        alignItems: "center",
        padding: "16px 18px",
        ...simplePanelStyle(theme),
        borderColor: C_RUST,
        boxShadow: `4px 4px 0 0 ${C_INK}`,
      }}
    >
      <div>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 13,
            letterSpacing: "0.10em",
            color: C_RUST,
            textTransform: "uppercase",
            marginBottom: 6,
          }}
        >
          {headline}
        </div>
        <div
          style={{
            fontFamily: theme.fontBody,
            fontSize: 16,
            color: theme.ink,
            lineHeight: 1.3,
          }}
        >
          {sub}
        </div>
      </div>
      <PixelButton kind="primary" large onClick={onClaim} disabled={busy}>
        {busy ? "CLAIMING…" : "CLAIM & LEAVE"}
      </PixelButton>
    </section>
  );
}

function FinalBar({
  label,
  busy,
  onClick,
}: {
  label: string;
  busy: boolean;
  onClick: () => void;
}) {
  return (
    <section
      style={{
        display: "flex",
        justifyContent: "flex-end",
        padding: "16px 18px",
        ...simplePanelStyle(pixelTheme),
      }}
    >
      <PixelButton kind="primary" large onClick={onClick} disabled={busy}>
        {busy ? "CLAIMING…" : label}
      </PixelButton>
    </section>
  );
}

function PixelButton({
  kind = "default",
  large = false,
  children,
  onClick,
  disabled,
  title,
}: {
  kind?: "default" | "primary" | "danger";
  large?: boolean;
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  const theme = pixelTheme;
  const palette = (() => {
    switch (kind) {
      case "primary":
        return { bg: theme.accent, fg: C_PARCHMENT, edge: C_INK };
      case "danger":
        return { bg: C_PLUM, fg: C_PARCHMENT, edge: C_INK };
      default:
        return { bg: theme.panel, fg: theme.inkDim, edge: theme.panelEdge };
    }
  })();
  // aria-disabled instead of the native disabled attribute so a busy
  // round-trip doesn't blur the focused button (the same anti-pattern
  // we standardised on app-wide via `logic/inert.ts`).
  return (
    <button
      type="button"
      onClick={(e) => {
        if (disabled) {
          e.preventDefault();
          return;
        }
        onClick();
      }}
      aria-disabled={disabled || undefined}
      title={title}
      style={{
        fontFamily: theme.fontDisplay,
        fontSize: large ? 13 : 11,
        letterSpacing: large ? "0.12em" : "0.10em",
        textTransform: "uppercase",
        cursor: disabled ? "not-allowed" : "pointer",
        background: palette.bg,
        color: palette.fg,
        border: `2px solid ${palette.edge}`,
        padding: large ? "14px 22px" : "10px 14px",
        opacity: disabled ? 0.5 : 1,
        boxShadow: disabled ? undefined : `2px 2px 0 0 ${C_INK}`,
      }}
    >
      {children}
    </button>
  );
}

// ─── Modals ─────────────────────────────────────────────────────────

function RewardsModal({
  payload,
  onClose,
}: {
  payload: api.ArenaClaimResponse;
  onClose: () => void;
}) {
  const theme = pixelTheme;
  // The server flips `:lost` → status "abandoned" at claim time so a
  // second claim can't re-grant. We carry the *kind* the player
  // initiated through the payload by looking at the granted set
  // shape and the final status.
  const wasCleared = payload.run.status === "cleared";
  const eyebrow = wasCleared
    ? "◆ ANTAR BATTLE ARENA · CLEARED"
    : "◆ CLAIM & LEAVE THE ARENA";
  const title = wasCleared
    ? "YOU CLEARED THE BRACKET."
    : payload.granted.currencies.length === 0 &&
        payload.granted.items.length === 0
      ? "THE CROWD GOES QUIET."
      : "YOU WALKED AWAY WITH WHAT YOU'D WON.";

  return (
    <ModalVeil onClose={onClose} ariaLabel="Arena rewards">
      <div
        style={{
          background: theme.panel,
          border: `3px solid ${C_INK}`,
          boxShadow: `inset 0 0 0 2px ${C_PARCHMENT}, inset 0 0 0 4px ${C_PLUM}, 6px 6px 0 0 ${C_INK}`,
          maxWidth: 560,
          width: "100%",
          padding: "22px 22px 18px",
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 9,
            letterSpacing: "0.18em",
            color: C_OXBLOOD,
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          {eyebrow}
        </div>
        <h2
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 22,
            letterSpacing: "0.04em",
            color: theme.ink,
            margin: "0 0 12px",
          }}
        >
          {title}
        </h2>
        <div
          style={{
            background: "rgba(99, 199, 77, 0.10)",
            border: `2px solid ${C_FOREST}`,
            padding: 12,
            marginBottom: 16,
          }}
        >
          <h4
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 9,
              letterSpacing: "0.10em",
              color: C_FOREST,
              margin: "0 0 8px",
              textTransform: "uppercase",
            }}
          >
            YOU TAKE HOME
          </h4>
          {payload.granted.currencies.length === 0 &&
            payload.granted.items.length === 0 && (
              <div style={{ color: theme.inkFaint }}>Nothing.</div>
            )}
          {payload.granted.currencies.map((c, i) => (
            <ItemLine
              key={`c${i}`}
              icon={
                <CurrencyIcon
                  kind={c.currency === "gold" ? "gold" : "juice"}
                  size={24}
                  tint={c.currency === "gold" ? C_AMBER : E32.mulberry}
                />
              }
              label={
                <>
                  <b>{c.amount}</b>{" "}
                  {c.currency === "gold" ? "GOLD" : "mL VOID JUICE"}
                </>
              }
            />
          ))}
          {payload.granted.items.map((it, i) => (
            <ItemLine
              key={`i${i}`}
              icon={<ItemSprite id={it.item_id} size={32} />}
              label={`${it.count}× ${humanizeItemId(it.item_id)}`}
            />
          ))}
        </div>
        {payload.granted.held.length > 0 && (
          <div
            style={{
              padding: "8px 10px",
              background: C_PARCHMENT,
              border: `1px dashed ${theme.panelEdge}`,
              fontFamily: theme.fontBody,
              fontSize: 14,
              color: theme.inkFaint,
              marginBottom: 12,
            }}
          >
            Your bag was full — Thomas Anton is holding{" "}
            <b style={{ color: theme.ink }}>
              {payload.granted.held
                .map((h) => `${h.count}× ${humanizeItemId(h.item_id)}`)
                .join(", ")}
            </b>{" "}
            at the gate.
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <PixelButton kind="primary" onClick={onClose}>
            OK
          </PixelButton>
        </div>
      </div>
    </ModalVeil>
  );
}

function ConfirmAbandonModal({
  wins,
  rewardsTable,
  currentRound,
  onCancel,
  onConfirm,
}: {
  wins: number;
  rewardsTable: api.ArenaRewardSlot[];
  currentRound: number;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const theme = pixelTheme;
  const lockedRounds =
    wins > 1 ? Array.from({ length: wins - 1 }, (_, i) => i + 1) : [];
  const lockedSlots = lockedRounds
    .map((r) => rewardsTable[r - 1])
    .filter((s): s is api.ArenaRewardSlot => Boolean(s));
  const goldTake = lockedSlots.reduce(
    (acc, s) =>
      acc +
      s.currencies
        .filter((c) => c.currency === "gold")
        .reduce((a, c) => a + c.amount, 0),
    0,
  );
  const vjTake = lockedSlots.reduce(
    (acc, s) =>
      acc +
      s.currencies
        .filter((c) => c.currency === "void_juice")
        .reduce((a, c) => a + c.amount, 0),
    0,
  );
  const itemsTake = lockedSlots.flatMap((s) => s.items);

  const atRiskSlot = wins >= 1 ? rewardsTable[wins - 1] : null;

  return (
    <ModalVeil onClose={onCancel} ariaLabel="Confirm abandon arena run">
      <div
        style={{
          background: theme.panel,
          border: `3px solid ${C_INK}`,
          boxShadow: `inset 0 0 0 2px ${C_PARCHMENT}, inset 0 0 0 4px ${C_PLUM}, 6px 6px 0 0 ${C_INK}`,
          maxWidth: 560,
          width: "100%",
          padding: "22px 22px 18px",
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 9,
            letterSpacing: "0.18em",
            color: C_OXBLOOD,
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          ◆ CLAIM & LEAVE THE ARENA
        </div>
        <h2
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 22,
            letterSpacing: "0.04em",
            color: theme.ink,
            margin: "0 0 12px",
          }}
        >
          WALK AWAY WITH WHAT YOU'VE WON?
        </h2>
        <p
          style={{
            fontFamily: theme.fontBody,
            fontSize: 18,
            color: theme.ink,
            margin: "0 0 16px",
            lineHeight: 1.4,
          }}
        >
          You'll skip Round {currentRound} and forfeit the rest of the gauntlet.
          The crowd will boo. Your purse will be heavier.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              background: "rgba(99, 199, 77, 0.10)",
              border: `2px solid ${C_FOREST}`,
              padding: 10,
            }}
          >
            <h4
              style={{
                fontFamily: theme.fontDisplay,
                fontSize: 9,
                letterSpacing: "0.10em",
                color: C_FOREST,
                margin: "0 0 8px",
                textTransform: "uppercase",
              }}
            >
              YOU TAKE HOME
            </h4>
            <ItemLine
              icon={<CurrencyIcon kind="gold" size={24} tint={C_AMBER} />}
              label={
                <>
                  <b>{goldTake}</b> GOLD
                </>
              }
            />
            {vjTake > 0 && (
              <ItemLine
                icon={<CurrencyIcon kind="juice" size={24} tint={E32.mulberry} />}
                label={
                  <>
                    <b>{vjTake}</b> mL VOID JUICE
                  </>
                }
              />
            )}
            {itemsTake.length === 0 && vjTake === 0 && (
              <ItemLine
                icon={null}
                label={
                  <span style={{ color: theme.inkFaint }}>No items.</span>
                }
              />
            )}
            {itemsTake.map((it, i) => (
              <ItemLine
                key={i}
                icon={<ItemSprite id={it.item_id} size={32} />}
                label={`${it.count}× ${humanizeItemId(it.item_id)}`}
              />
            ))}
          </div>

          <div
            style={{
              background: C_PARCHMENT,
              border: `2px solid ${theme.panelEdge}`,
              padding: 10,
            }}
          >
            <h4
              style={{
                fontFamily: theme.fontDisplay,
                fontSize: 9,
                letterSpacing: "0.10em",
                color: theme.inkFaint,
                margin: "0 0 8px",
                textTransform: "uppercase",
              }}
            >
              YOU GIVE UP
            </h4>
            {/* Show the at-risk reward(s) — those are what's actually
                forfeit when the player walks. The "upcoming round" /
                opponent name isn't lost; it's just a fight they're
                choosing not to take. */}
            {atRiskSlot &&
              atRiskSlot.currencies.map((c, i) => (
                <ItemLine
                  key={`gc${i}`}
                  icon={
                    <CurrencyIcon
                      kind={c.currency === "gold" ? "gold" : "juice"}
                      size={24}
                      tint={c.currency === "gold" ? C_AMBER : E32.mulberry}
                    />
                  }
                  label={
                    <span style={{ color: theme.inkFaint }}>
                      {c.amount} {c.currency === "gold" ? "gold" : "mL VJ"}
                    </span>
                  }
                />
              ))}
            {atRiskSlot &&
              atRiskSlot.items.map((it, i) => (
                <ItemLine
                  key={`gi${i}`}
                  icon={<ItemSprite id={it.item_id} size={32} />}
                  label={
                    <span style={{ color: theme.inkFaint }}>
                      {it.count}× {humanizeItemId(it.item_id)}
                    </span>
                  }
                />
              ))}
            {/* "Plus N more rounds of rewards" — N counts the
                rounds the player won't fight (currentRound..10),
                excluding the at-risk reward which is shown above. */}
            {10 - currentRound > 0 && (
              <ItemLine
                icon={null}
                label={
                  <span style={{ color: theme.inkFaint }}>
                    Plus {10 - currentRound} more{" "}
                    {10 - currentRound === 1 ? "round" : "rounds"} of rewards.
                  </span>
                }
              />
            )}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <PixelButton onClick={onCancel}>STAY IN ARENA</PixelButton>
          <PixelButton kind="primary" onClick={onConfirm}>
            CLAIM & LEAVE
          </PixelButton>
        </div>
      </div>
    </ModalVeil>
  );
}

function ItemLine({ icon, label }: { icon: ReactNode; label: ReactNode }) {
  const theme = pixelTheme;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontFamily: theme.fontBody,
        fontSize: 16,
        color: theme.ink,
        padding: "3px 0",
      }}
    >
      {icon}
      <span>{label}</span>
    </div>
  );
}

function ModalVeil({
  children,
  onClose,
  ariaLabel,
}: {
  children: ReactNode;
  /** If provided, Escape closes and focus returns to the previously
   *  focused element on unmount. */
  onClose?: () => void;
  ariaLabel?: string;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalFocus(dialogRef, onClose ?? (() => {}), {
    closeOnEscape: !!onClose,
  });
  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      tabIndex={-1}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(24, 20, 37, 0.78)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        padding: 24,
      }}
    >
      {children}
    </div>
  );
}

function humanizeItemId(id: string): string {
  return id.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Error banner ───────────────────────────────────────────────────

function ErrorBanner({ text }: { text: string }) {
  const theme = pixelTheme;
  return (
    <div
      style={{
        padding: 10,
        border: `2px solid ${E32.scarlet}`,
        color: E32.scarlet,
        fontFamily: theme.fontDisplay,
        fontSize: 11,
        letterSpacing: theme.letterSpacing,
      }}
    >
      {text}
    </div>
  );
}

// ─── One-time animation styles ──────────────────────────────────────
//
// The reference CSS defines `@keyframes bp-pulse` for the current
// orb. Inline styles can't declare keyframes, so we follow the same
// pattern the logo's `indev-banner` splash uses: inject a single
// <style> element into <head> on first render, with a unique id so
// repeated mounts don't stack copies.

const ORB_PULSE_CSS = `
@keyframes arena-bp-pulse {
  0%, 100% {
    box-shadow:
      inset 2px 2px 0 rgba(255,255,255,0.25),
      inset -2px -2px 0 rgba(0,0,0,0.30),
      0 0 0 2px ${C_INK},
      4px 4px 0 ${C_INK};
  }
  50% {
    box-shadow:
      inset 2px 2px 0 rgba(255,255,255,0.25),
      inset -2px -2px 0 rgba(0,0,0,0.30),
      0 0 0 2px ${C_BUTTER},
      4px 4px 0 ${C_INK};
  }
}
.arena-bp-orb-next {
  animation: arena-bp-pulse 1.8s steps(2, start) infinite;
}
`;

if (
  typeof document !== "undefined" &&
  !document.getElementById("arena-bp-pulse-styles")
) {
  const el = document.createElement("style");
  el.id = "arena-bp-pulse-styles";
  el.textContent = ORB_PULSE_CSS;
  document.head.appendChild(el);
}

function errorMessage(e: unknown, fallback: string): string {
  if (e instanceof api.ApiError && e.body && typeof e.body === "object") {
    const code = (e.body as { error?: string }).error;
    switch (code) {
      case "no_ticket":
        return "You don't have an Arena Ticket.";
      case "already_in_run":
        return "You already have an active run.";
      case "held_items_pending":
        return "Collect your held winnings from the clerk first.";
      case "deck_already_locked":
        return "Your deck is locked for this run — you can't switch.";
      case "no_more_rounds":
        return "You've cleared the bracket — claim your rewards.";
      case "pool_too_small_easy":
      case "pool_too_small_medium":
      case "pool_too_small_hard":
      case "pool_too_small_boss":
        return "The arena is short on fighters today. Try again later.";
      case "in_arena_run":
        return "You're already in an arena run.";
      default:
        return code ?? fallback;
    }
  }
  return e instanceof Error ? e.message : fallback;
}
